/**
 * Dashboard Page Component
 *
 * Main dashboard page that provides role-based routing to appropriate
 * dashboard views. Handles loading states, error handling, and user
 * authentication verification.
 *
 * @module components/pages/DashboardPage
 */

import React, { Suspense, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useTheme } from '@/hooks/useTheme';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/Button';
import { TitleText } from '@/components/ui/TitleText';
import { BodyText } from '@/components/ui/BodyText';

// Lazy load dashboard components for better performance
const ProviderDashboardPage = React.lazy(() => import('./ProviderDashboardPage'));
const DieticianDashboardPage = React.lazy(() =>
  import('./DieticianDashboardPage').catch(() => ({ default: () => <div>Dietician Dashboard Coming Soon</div> }))
);
const PatientDashboardPage = React.lazy(() =>
  import('@/components/patient-dashboard/PatientDashboardPage').catch(() => ({
    default: () => <div>Patient Dashboard Coming Soon</div>,
  }))
);

/**
 * Loading component for dashboard content
 */
const DashboardLoading: React.FC = () => {

  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
        <BodyText variant="secondary">Loading your dashboard...</BodyText>
      </div>
    </div>
  );
};

/**
 * Error component for dashboard failures
 */
interface DashboardErrorProps {
  error: Error;
  onRetry: () => void;
}

const DashboardError: React.FC<DashboardErrorProps> = ({ error, onRetry }) => {
  const { isDark } = useTheme();

  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center space-y-6 max-w-md mx-auto px-4">
        <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
          isDark ? 'bg-error-900/20' : 'bg-error-50'
        }`}>
          <svg
            className={`w-8 h-8 ${isDark ? 'text-error-400' : 'text-error-500'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>

        <div className="space-y-2">
          <TitleText level={3}>Unable to Load Dashboard</TitleText>
          <BodyText variant="secondary" className="text-sm">
            {error.message || 'An unexpected error occurred while loading your dashboard.'}
          </BodyText>
        </div>

        <div className="space-y-3">
          <Button
            onClick={onRetry}
            variant="primary"
            size="md"
            className="w-full"
          >
            Try Again
          </Button>

          <BodyText variant="muted" size="sm">
            If the problem persists, please contact support.
          </BodyText>
        </div>
      </div>
    </div>
  );
};

/**
 * Main Dashboard Page Component
 */
export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();

// Set document title
  useEffect(() => {
    document.title = 'Dashboard - BiteWise Healthcare Platform';
  }, []);

  const {
    profile,
    isLoading,
    error,
    fetchProfile,
    clearError,
  } = useUserProfile({
    autoFetch: true,
  });

  // Handle retry for errors
  const handleRetry = () => {
    clearError();
    fetchProfile();
  };

  // Show loading state while fetching profile
  if (isLoading) {
    return (
      <Layout>
        <DashboardLoading />
      </Layout>
    );
  }

  // Handle errors
  if (error) {
    // Handle authentication errors - redirect to login
    if (error.status === 401) {
      navigate('/login', { replace: true });
      return null;
    }

    // Handle not found errors - redirect to home
    if (error.status === 404) {
      navigate('/', { replace: true });
      return null;
    }

    // Show error component for other errors
    return (
      <Layout>
        <DashboardError error={error} onRetry={handleRetry} />
      </Layout>
    );
  }

  // If no profile data, redirect to login
  if (!profile) {
    navigate('/login', { replace: true });
    return null;
  }

  // Render role-specific dashboard with Suspense for lazy loading
  const renderDashboard = () => {
    switch (profile.role) {
      case 'provider':
        return (
          <Suspense fallback={<DashboardLoading />}>
            <ProviderDashboardPage />
          </Suspense>
        );

      case 'dietician':
        return (
          <Suspense fallback={<DashboardLoading />}>
            <Layout>
              <DieticianDashboardPage />
            </Layout>
          </Suspense>
        );

      case 'patient':
        return (
          <Suspense fallback={<DashboardLoading />}>
            <Layout>
              <PatientDashboardPage />
            </Layout>
          </Suspense>
        );

      case 'admin':
        // Redirect admin users to external admin panel
        window.location.href = "/admin/";
        return null;

      default:
        // For unsupported roles, redirect to home
        navigate('/', { replace: true });
        return null;
    }
  };

  return renderDashboard();
};

export default DashboardPage;